import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const API = process.env.REACT_APP_BACKEND_URL || '';

const FALLBACK_CONTENT = `## 1. Information We Collect

We collect information you provide directly, including: name, email address, company name, phone number, physical address, GPS coordinates (optional), hive and apiary data, inspection records, and uploaded photos.

## 2. How We Use Your Information

We use your information to: provide and improve our beekeeping management services, process subscriptions and payments, communicate with you about your account, display community features (with your consent), and generate aggregated analytics.

## 3. Data Storage

Your data is stored on secure cloud servers. We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, or destruction.

## 4. Data Sharing

We do not sell your personal data. We may share data with: service providers who assist in operating our platform, legal authorities when required by law, and other users only when you explicitly opt-in to community features.

## 5. Your Rights

You have the right to: access your personal data, correct inaccurate data, request deletion of your data, export your data, and withdraw consent for optional features.

## 6. Cookies and Tracking

We use essential cookies for authentication and session management. We do not use third-party tracking or advertising cookies.

## 7. GPS and Location Data

GPS coordinates are collected only if you enable location features. This data is used for apiary mapping and optional community features (Find Beekeepers). You can disable location sharing at any time in your profile settings.

## 8. Data Retention

We retain your data for as long as your account is active. Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law.

## 9. Children's Privacy

Our service is not intended for use by individuals under the age of 16. We do not knowingly collect personal data from children.

## 10. Changes to This Policy

We may update this Privacy Policy from time to time. We will notify you of significant changes through the application.

## 11. Contact

For privacy-related questions or data deletion requests, contact: macmanclan@gmail.com`;

const PrivacyPolicy = () => {
  const [appSettings, setAppSettings] = useState({ app_name: 'Thrive' });
  const [content, setContent] = useState('');
  const [version, setVersion] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/app-settings/public`)
      .then(res => res.json())
      .then(data => data.app_name && setAppSettings(data))
      .catch(() => {});

    fetch(`${API}/api/legal-documents/privacy`)
      .then(res => res.json())
      .then(data => {
        if (data.content && data.is_published !== false) {
          setContent(data.content);
          setVersion(data.version || '');
        } else {
          setContent(FALLBACK_CONTENT);
        }
      })
      .catch(() => setContent(FALLBACK_CONTENT))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="bg-white border-b border-stone-200 py-4 px-6 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/trial-signup" className="flex items-center gap-2 text-stone-600 hover:text-stone-800">
            <ArrowLeft className="w-4 h-4" />
            Back to Sign Up
          </Link>
          <div className="flex items-center gap-2">
            <span className="font-bold text-stone-800">{appSettings.app_name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-stone-800 mb-2">Privacy Policy</h1>
        <p className="text-stone-500 mb-8">{version ? `Version ${version}` : 'Current version'}</p>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-600 mr-2" />
            <span className="text-stone-500">Loading...</span>
          </div>
        ) : (
          <div className="prose prose-stone max-w-none">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicy;
